/**
 * API Client Service
 * Provides HTTP methods for communicating with the backend API
 * Handles authentication, error formatting, and request/response serialization
 */

import env from '@/utils/env';
import { auth } from './firebaseService';
import logger from './logger';

/**
 * Get Firebase ID token from current user
 * @returns {Promise<string|null>} ID token or null if not authenticated
 */
async function getAuthToken() {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    return await user.getIdToken();
  } catch (err) {
    logger.warn('Unable to get ID token', err);
    return null;
  }
}

/**
 * Get session ID from local storage
 * @returns {string|null} Session ID or null
 */
function getSessionId() {
  return localStorage.getItem('sessionId');
}

/**
 * Set session ID in local storage
 * @param {string} sessionId - Session ID to store
 */
export function setSessionId(sessionId) {
  if (sessionId) {
    localStorage.setItem('sessionId', sessionId);
  } else {
    localStorage.removeItem('sessionId');
  }
}

/**
 * Build request headers with authentication if available
 * @param {object} additionalHeaders - Additional headers to include
 * @returns {Promise<object>} Headers object
 */
async function buildHeaders(additionalHeaders = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...additionalHeaders
  };

  const token = await getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add session ID header if available
  const sessionId = getSessionId();
  if (sessionId) {
    headers['X-Session-ID'] = sessionId;
  }

  return headers;
}

/**
 * Parse API error response into a structured error object
 * @param {Response} response - Fetch response object
 * @returns {Promise<Error>} Error with structured message
 */
async function parseError(response) {
  let errorData;
  try {
    errorData = await response.json();
  } catch {
    // Response body is not JSON
    return new Error(`API error: ${response.status} ${response.statusText}`);
  }

  // Expecting backend error envelope: { error: { code, message, details? } }
  if (errorData.error) {
    const err = new Error(errorData.error.message || 'Une erreur est survenue');
    err.code = errorData.error.code;
    err.status = response.status;
    err.details = errorData.error.details;

    // Handle session_expired error - clear session and force re-login
    if (err.code === 'session_expired') {
      setSessionId(null);
      // Dispatch a custom event to notify the app that session has expired
      window.dispatchEvent(
        new CustomEvent('session-expired', {
          detail: { message: err.message }
        })
      );
    }

    return err;
  }

  return new Error(errorData.message || `API error: ${response.status}`);
}

/**
 * GET request
 * @param {string} path - API path (e.g., '/themes')
 * @param {object} options - Additional fetch options
 * @returns {Promise<any>} Response JSON
 */
export async function apiGet(path, queryParams = {}, options = {}) {
  const headers = await buildHeaders(options.headers);
  const { headers: _, ...fetchOptions } = options; // Exclude headers from options spread

  const queryString = new URLSearchParams(queryParams).toString();
  const fullUrl = `${env.apiBaseUrl}${path}${queryString ? '?' + queryString : ''}`;

  console.log('[apiClient] GET request:', { path, queryParams, queryString, fullUrl });

  const res = await fetch(fullUrl, {
    method: 'GET',
    headers,
    ...fetchOptions
  });

  if (!res.ok) {
    throw await parseError(res);
  }

  return res.json();
}

/**
 * POST request
 * @param {string} path - API path (e.g., '/stripe/create-checkout-session')
 * @param {object} body - Request body (will be JSON stringified)
 * @param {object} options - Additional fetch options
 * @returns {Promise<any>} Response JSON
 */
export async function apiPost(path, body = {}, options = {}) {
  const headers = await buildHeaders(options.headers);
  const { headers: _, ...fetchOptions } = options; // Exclude headers from options spread
  const res = await fetch(`${env.apiBaseUrl}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    ...fetchOptions
  });

  if (!res.ok) {
    throw await parseError(res);
  }

  return res.json();
}

/**
 * PATCH request
 * @param {string} path - API path
 * @param {object} body - Request body (will be JSON stringified)
 * @param {object} options - Additional fetch options
 * @returns {Promise<any>} Response JSON
 */
export async function apiPatch(path, body = {}, options = {}) {
  const headers = await buildHeaders(options.headers);
  const { headers: _, ...fetchOptions } = options; // Exclude headers from options spread
  const res = await fetch(`${env.apiBaseUrl}${path}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
    ...fetchOptions
  });

  if (!res.ok) {
    throw await parseError(res);
  }

  return res.json();
}

/**
 * DELETE request
 * @param {string} path - API path
 * @param {object} options - Additional fetch options
 * @returns {Promise<any>} Response JSON
 */
export async function apiDelete(path, options = {}) {
  const headers = await buildHeaders(options.headers);
  const { headers: _, ...fetchOptions } = options; // Exclude headers from options spread
  const res = await fetch(`${env.apiBaseUrl}${path}`, {
    method: 'DELETE',
    headers,
    ...fetchOptions
  });

  if (!res.ok) {
    throw await parseError(res);
  }

  // Some DELETE endpoints return 204 No Content
  if (res.status === 204) {
    return null;
  }

  return res.json();
}

/**
 * Legacy default export for backward compatibility
 */
export default {
  get: apiGet,
  post: apiPost,
  patch: apiPatch,
  delete: apiDelete
};
